import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { motion } from 'framer-motion'
import { ChevronRightIcon, DocumentTextIcon, ArrowUpIcon } from '@heroicons/react/24/outline'

const sections = [
  { id: 'acceptance', number: 1, title: 'Acceptance of Terms' },
  { id: 'license', number: 2, title: 'Use License' },
  { id: 'disclaimer', number: 3, title: 'Disclaimer' },
  { id: 'limitations', number: 4, title: 'Limitations' },
  { id: 'accuracy', number: 5, title: 'Accuracy of Materials' },
  { id: 'links', number: 6, title: 'Links' },
  { id: 'modifications', number: 7, title: 'Modifications' },
  { id: 'governing-law', number: 8, title: 'Governing Law' },
]

export default function TermsPage() {
  usePageTitle('Terms of Service')
  const [activeSection, setActiveSection] = useState(sections[0].id)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const sectionRefs = useRef({})

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400)

      const scrollPosition = window.scrollY + 120
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = sectionRefs.current[sections[i].id]
        if (el && el.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id)
          break
        }
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id]
    if (el) {
      const offset = 80
      const top = el.offsetTop - offset
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="py-8 sm:py-12"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-text-muted mb-6">
          <Link to="/" className="link-subtle hover:text-text">Home</Link>
          <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="text-text font-medium">Terms of Service</span>
        </nav>

        {/* Header */}
        <div className="flex items-start gap-3.5 mb-8">
          <div className="rounded-xl bg-surface-100 dark:bg-surface-200 p-2.5 flex-shrink-0
            ring-4 ring-surface-100/50 dark:ring-surface-200/30">
            <DocumentTextIcon className="h-6 w-6 text-text-secondary" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-text leading-tight">Terms of Service</h1>
            <p className="text-text-muted mt-1 text-sm">Last updated: March 2026</p>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-8">
          {/* Sidebar TOC — desktop only */}
          <aside className="hidden lg:block">
            <nav className="sticky top-24">
              <p className="text-xs font-bold uppercase tracking-wider text-text-muted mb-3">
                On this page
              </p>
              <ul className="space-y-0.5">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left text-sm py-1.5 px-3 rounded-lg transition-all duration-150
                        ${activeSection === section.id
                          ? 'bg-primary-50 dark:bg-primary-50 text-primary-700 dark:text-primary-600 font-medium'
                          : 'text-text-muted hover:text-text hover:bg-surface-100 dark:hover:bg-surface-200'
                        }`}
                    >
                      {section.number}. {section.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Content */}
          <div className="card p-6 sm:p-8 lg:p-10">
            {/* Mobile TOC */}
            <details className="lg:hidden mb-8 group">
              <summary className="flex items-center justify-between cursor-pointer
                text-sm font-semibold text-text p-3 rounded-lg
                bg-surface-50 dark:bg-surface-100 border border-border
                hover:bg-surface-100 dark:hover:bg-surface-200 transition-colors">
                <span>Table of Contents</span>
                <ChevronRightIcon className="h-4 w-4 text-text-muted transition-transform duration-200
                  group-open:rotate-90" />
              </summary>
              <nav className="mt-2 pl-3 border-l-2 border-border">
                <ul className="space-y-1 py-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className="text-sm text-text-secondary hover:text-primary-600 dark:hover:text-primary-500
                          py-1 transition-colors w-full text-left"
                      >
                        {section.number}. {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </details>

            <div className="prose-app space-y-10">
              <section ref={(el) => (sectionRefs.current['acceptance'] = el)} id="acceptance">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">01</span>
                  Acceptance of Terms
                </h2>
                <p>
                  By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['license'] = el)} id="license">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">02</span>
                  Use License
                </h2>
                <p>
                  Permission is granted to temporarily download one copy of the materials (information or software) on AppCerc LLC's website for personal, non-commercial transitory viewing only.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['disclaimer'] = el)} id="disclaimer">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">03</span>
                  Disclaimer
                </h2>
                <p>
                  The materials on AppCerc LLC's website are provided on an 'as is' basis. AppCerc LLC makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['limitations'] = el)} id="limitations">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">04</span>
                  Limitations
                </h2>
                <p>
                  In no event shall AppCerc LLC or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on AppCerc LLC's website.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['accuracy'] = el)} id="accuracy">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">05</span>
                  Accuracy of Materials
                </h2>
                <p>
                  The materials appearing on AppCerc LLC's website could include technical, typographical, or photographic errors. AppCerc LLC does not warrant that any of the materials on its website are accurate, complete, or current.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['links'] = el)} id="links">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">06</span>
                  Links
                </h2>
                <p>
                  AppCerc LLC has not reviewed all of the sites linked to its website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by AppCerc LLC of the site.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['modifications'] = el)} id="modifications">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">07</span>
                  Modifications
                </h2>
                <p>
                  AppCerc LLC may revise these terms of service for its website at any time without notice. By using this website you are agreeing to be bound by the then current version of these terms of service.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['governing-law'] = el)} id="governing-law">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">08</span>
                  Governing Law
                </h2>
                <p>
                  These terms and conditions are governed by and construed in accordance with the laws and you irrevocably submit to the exclusive jurisdiction of the courts in that location.
                </p>
              </section>
            </div>
          </div>
        </div>

        {/* Footer link */}
        <div className="mt-8 text-center">
          <Link to="/privacy" className="link text-sm inline-flex items-center gap-1.5">
            View our Privacy Policy
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {/* Back to top button */}
      {showBackToTop && (
        <motion.button
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-sticky
            p-3 rounded-full bg-primary-600 text-white shadow-lg
            hover:bg-primary-700 active:scale-95
            transition-all duration-200
            min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Back to top"
        >
          <ArrowUpIcon className="h-5 w-5" />
        </motion.button>
      )}
    </motion.div>
  )
}
